import { createContext, useContext } from 'react';
import { Locale, useConfig } from '../config';
import { austrianTranslations } from './de-AT';
import { germanTranslations } from './de-DE';
import { danishTranslations } from './dk';
import { englishTranslations } from './en';
import { dutchTranslations } from './nl';
import { norwegianTranslations } from './no';
import { swedishTranslations } from './sv';

export type Translations = typeof swedishTranslations;

export function currentLocalization(lang: Locale): Translations {
	switch (lang) {
		case 'sv-SE':
			return swedishTranslations;
		case 'nb-NO':
			return norwegianTranslations;
		case 'da-DK':
			return danishTranslations;
		case 'de-AT':
			return austrianTranslations;
		case 'de-DE':
			return germanTranslations;
		case 'nl-NL':
			return dutchTranslations;
		case 'en-GB':
			return englishTranslations;
		default:
			return englishTranslations;
	}
}

interface Formatter {
	asCurrency(value: number, fractionDigits?: number, stripFractionZeros?: boolean): string;
	asPrice(
		value: number,
		fractionDigits?: number,
		stripFractionZeros?: boolean,
		displayCurrencyString?: boolean // e.g. 'kr'
	): string;
	dateTimeFormat(date: Date, withoutTime?: boolean): string;
	asZip(zip: string): string;
	asLocaleNumber(num: number): string;
}

export function useFormatting(): Formatter {
	const locale = useContext(LocalizationContext).locale;
	const { currencyCode } = useConfig();

	return {
		asCurrency(amount, fractionDigits = 2, stripFractionZeros = true) {
			let currencyString = Intl.NumberFormat(locale, {
				style: 'decimal',
				minimumFractionDigits: fractionDigits,
				maximumFractionDigits: fractionDigits,
			}).format(amount);
			if (stripFractionZeros) currencyString = currencyString.replace(',00', '');
			return currencyString;
		},

		/*
		 * Intl's currency style already knows how each market writes one, and the differences are
		 * exactly the ones that were being got wrong by hand:
		 *
		 *   de-DE   0,10 €      nl-NL   € 0,10      sv-SE / nb-NO   0,10 kr      da-DK   0,10 kr.
		 *
		 * Dutch puts the symbol first where German puts it after; Danish abbreviates the krone
		 * with a full stop where Swedish and Norwegian do not. The krona markets were built by
		 * hand from a decimal and the symbol out of config, which could say neither.
		 *
		 * It also joins the amount to the symbol with U+00A0, so a price cannot be broken across
		 * two lines - which no amount of markup around the call could have achieved, because the
		 * space was inside the string.
		 *
		 * displayCurrencyString is honoured only where it ever was. A caller passing false adds
		 * the symbol itself, from config, and skips that for EUR precisely because this returned
		 * it anyway; honouring the flag uniformly would take the euro off those prices.
		 */
		asPrice(amount, fractionDigits = 2, stripFractionZeros = true, displayCurrencyString = true) {
			const withSymbol = displayCurrencyString || currencyCode === 'EUR';
			let priceString = Intl.NumberFormat(locale, {
				style: withSymbol ? 'currency' : 'decimal',
				currency: currencyCode,
				minimumFractionDigits: fractionDigits,
				maximumFractionDigits: fractionDigits,
			}).format(amount);
			if (stripFractionZeros) priceString = priceString.replace(',00', '');
			return priceString;
		},

		dateTimeFormat(date, withoutTime) {
			return Intl.DateTimeFormat(locale, {
				dateStyle: 'short',
				timeStyle: withoutTime ? undefined : 'short',
			}).format(date);
		},

		asZip(rawZip) {
			// normalize zip
			const zip = rawZip.replace(/\s+/g, '').toUpperCase();

			switch (locale) {
				case 'sv-SE':
					// 5 digits, space after 3 (e.g. 123 45)
					return zip.length > 3 ? `${zip.slice(0, 3)} ${zip.slice(3)}` : zip;

				case 'nb-NO':
				case 'da-DK':
				case 'de-AT':
				case 'de-DE':
					// 4 or 5 digits, no space, i.e. raw (e.g. 1234, 12345)
					return zip;
				case 'nl-NL':
					// 4 digits + 2 letters, space between (e.g. 1234 AB)
					return zip.length > 4 ? `${zip.slice(0, 4)} ${zip.slice(4)}` : zip;
				case 'en-GB':
					// outward/inward separated by space before last 3 chars (e.g. SW1A 1AA)
					return zip.length > 3 ? `${zip.slice(0, zip.length - 3)} ${zip.slice(-3)}` : zip;
				default:
					return rawZip;
			}
		},

		asLocaleNumber(num) {
			return new Intl.NumberFormat(locale).format(num);
		},
	};
}

export function useTranslation(): Translations {
	return useContext(LocalizationContext);
}

export const LocalizationContext = createContext<Translations>(null as unknown as Translations);
